import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const SalarySettingContext = createContext();

export const SalarySettingProvider = ({ children }) => {
  const [salarySettings, setSalarySettings] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    include: "user,insurances,facilities,deductions",
    userId: "",
  });


  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    include: filterOptions.include || "user",
    "filter[user_id]": filterOptions.userId || "",
  });


  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const handleUserFilter = (userId) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      userId: userId,
      currentPage: 1,
    }));
  };



  const fetchSalarySettings = useCallback(async () => {
    setSalarySettings((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/salary-settings${queryString}`);
      if (response.success) {
        setSalarySettings({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setSalarySettings({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchSalarySettings();
  }, [fetchSalarySettings]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = salarySettings.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <SalarySettingContext.Provider
      value={{
        salarySettings,
        filterOptions,
        handleSearch,
        handleUserFilter,
        refetch: fetchSalarySettings,
        Pagination,

      }}
    >
      {children}
    </SalarySettingContext.Provider>
  );
};

export const useSalarySetting = () => {
  const context = useContext(SalarySettingContext);
  if (!context) {
    throw new Error(
      "useSalarySetting must be used within SalarySettingProvider"
    );
  }

  const fetchUserOptions = async (inputValue = "") => {
    const response = await getRequest(`/user?search=${inputValue}&limit=50&include=roles`);
    if (response.success) {
      // Filter users with role "user_hr"
      const hrUsers = response.data.filter(user =>
        user.roles && user.roles.some(role => role.name === "user_hr")
      );
      return hrUsers.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  const fetchInsuranceOptions = async (inputValue = "") => {
    const response = await getRequest(`/ahr/insurances?search=${inputValue}&limit=50`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  const fetchFacilityOptions = async (inputValue = "") => {
    const response = await getRequest(`/ahr/facilities?search=${inputValue}&limit=50`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  const fetchDeductionOptions = async (inputValue = "") => {
    const response = await getRequest(`/ahr/deductions?search=${inputValue}&limit=50`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  const loadUserOptions = useCallback(async (inputValue) => {
    return await fetchUserOptions(inputValue);
  }, []);

  const loadInsuranceOptions = useCallback(async (inputValue) => {
    return await fetchInsuranceOptions(inputValue);
  }, []);

  const loadFacilityOptions = useCallback(async (inputValue) => {
    return await fetchFacilityOptions(inputValue);
  }, []);

  const loadDeductionOptions = useCallback(async (inputValue) => {
    return await fetchDeductionOptions(inputValue);
  }, []);

  return {
    ...context,
    fetchUserOptions,
    fetchInsuranceOptions,
    fetchFacilityOptions,
    fetchDeductionOptions,
    loadUserOptions,
    loadInsuranceOptions,
    loadFacilityOptions,
    loadDeductionOptions,
  };
};
